import { useMemo, useState } from "react"
import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { ArrowRight, BookOpen, FileText, Newspaper, Search, Video } from "lucide-react"

export function ResourcesPage() {
  type Category = "all" | "blog" | "case-studies" | "whitepapers" | "guides" | "webinars" | "updates"

  const [category, setCategory] = useState<Category>("all")
  const [query, setQuery] = useState("")

  const categories: Array<{ slug: Category; label: string }> = [
    { slug: "all", label: "All resources" },
    { slug: "blog", label: "Blog" },
    { slug: "case-studies", label: "Case studies" },
    { slug: "whitepapers", label: "Whitepapers" },
    { slug: "guides", label: "Compliance guides" },
    { slug: "webinars", label: "Webinars" },
    { slug: "updates", label: "Product updates" },
  ]

  const items = useMemo(
    () => [
      {
        id: "blog",
        type: "blog" as const,
        icon: Newspaper,
        title: "Blog",
        description: "Latest insights, tips, and best practices for modern HR teams.",
        href: "/blog",
        tags: ["Trends", "Culture", "Leadership"],
      },
      {
        id: "case-studies",
        type: "case-studies" as const,
        icon: BookOpen,
        title: "Case studies",
        description: "Real-world stories from teams scaling HR operations across locations.",
        href: "/resources#case-studies",
        tags: ["ROI", "Implementation"],
      },
      {
        id: "whitepapers",
        type: "whitepapers" as const,
        icon: FileText,
        title: "Whitepapers",
        description: "Deep dives on HR ops, payroll modernization, and workforce analytics.",
        href: "/resources#whitepapers",
        tags: ["Research", "Enterprise"],
      },
      {
        id: "guides",
        type: "guides" as const,
        icon: FileText,
        title: "HR compliance guides",
        description: "Practical, updated checklists and compliance guidance (India + global).",
        href: "/resources#compliance",
        tags: ["DPDP", "GDPR", "Payroll"],
      },
      {
        id: "webinars",
        type: "webinars" as const,
        icon: Video,
        title: "Webinars",
        description: "Short, high-signal sessions on product, compliance, and best practices.",
        href: "/resources#webinars",
        tags: ["Live", "Training"],
      },
      {
        id: "updates",
        type: "updates" as const,
        icon: Newspaper,
        title: "Product updates",
        description: "What’s new: improvements, features, and reliability upgrades.",
        href: "/resources#updates",
        tags: ["Changelog", "Releases"],
      },
    ],
    []
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((it) => {
      const matchesCategory = category === "all" ? true : it.type === category
      if (!matchesCategory) return false
      if (!q) return true
      const hay = `${it.title} ${it.description} ${it.tags.join(" ")}`.toLowerCase()
      return hay.includes(q)
    })
  }, [items, category, query])

  const featured = items.slice(0, 3)

  return (
    <>
      <SEO
        title="Resources - Nestor"
        description="Explore resources for HR leaders: blog, whitepapers, case studies, compliance guides, webinars, and product updates."
      />

      {/* Top bar (docs-like) */}
      <section className="bg-gradient-to-b from-primary/10 via-background to-background py-10">
        <div className="container">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Resources</div>
              <div className="mt-1 text-xl font-semibold tracking-tight">
                Insights, guides, and updates
              </div>
            </div>
            <Button variant="outline" className="rounded-full" asChild>
              <Link to="/docs">View Documentation</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container py-10">
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* Left sidebar */}
          <aside className="lg:sticky lg:top-20 lg:h-[calc(100vh-96px)] lg:overflow-auto">
            <div className="rounded-2xl border bg-card p-3 shadow-sm">
              <div className="px-2 pb-2 pt-1 text-xs font-semibold text-muted-foreground">
                Search
              </div>
              <div className="relative px-2 pb-3">
                <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search resources..."
                  className="pl-10 rounded-xl"
                />
              </div>

              <div className="px-2 pb-2 pt-2 text-xs font-semibold text-muted-foreground">
                Categories
              </div>
              <nav className="grid gap-1 px-1 pb-2">
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setCategory(c.slug)}
                    className={cn(
                      "w-full text-left rounded-xl px-3 py-2 text-sm transition-colors",
                      c.slug === category
                        ? "bg-accent text-foreground"
                        : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Right content */}
          <div className="min-w-0">
            {/* Featured */}
            <div className="rounded-2xl border bg-card p-6 shadow-sm md:p-8">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Featured</div>
                  <h1 className="mt-1 text-2xl font-semibold tracking-tight">
                    Start here
                  </h1>
                </div>
                <Button className="rounded-full" asChild>
                  <Link to="/demo">
                    Request Demo <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {featured.map((r) => (
                  <Link key={r.id} to={r.href} className="group">
                    <div className="rounded-2xl border bg-background p-5 shadow-sm transition-colors hover:bg-muted/40">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                          <r.icon className="h-5 w-5 text-primary" />
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                      <div className="mt-4 font-medium">{r.title}</div>
                      <div className="mt-1 text-sm text-muted-foreground line-clamp-2">
                        {r.description}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Filtered list */}
            <div className="mt-6 rounded-2xl border bg-card p-6 shadow-sm md:p-8">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold tracking-tight">All resources</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Showing {filtered.length} result{filtered.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {category !== "all" && (
                    <Badge variant="secondary" className="rounded-full">
                      Category: {categories.find((c) => c.slug === category)?.label}
                    </Badge>
                  )}
                  {query.trim() && (
                    <Badge variant="outline" className="rounded-full">
                      Search: “{query.trim()}”
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {filtered.map((r) => (
                  <Link key={r.id} to={r.href} className="group">
                    <Card className="h-full border-border/50 shadow-sm transition-shadow hover:shadow-md">
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                              <r.icon className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{r.title}</CardTitle>
                              <CardDescription className="mt-1">{r.description}</CardDescription>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {r.tags.slice(0, 3).map((t) => (
                            <Badge key={t} variant="secondary" className="rounded-full">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}

                {filtered.length === 0 && (
                  <div className="rounded-2xl border bg-background p-8 text-center text-muted-foreground md:col-span-2">
                    No resources found. Try a different search or category.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
